<template>
    <div class="page-container" v-loading="loading">
        <!-- 头部区域 -->
        <div v-if="$slots.head" class="page-container-header">
            <div class="header-content">
                <slot name="head" />
            </div>
        </div>

        <!-- 主体内容区域 -->
        <div class="page-container-body" :class="bodyClass">
            <!-- 左侧区域 -->
            <div v-if="hasLeftSlot" class="page-container-side page-container-left">
                <div class="side-content" ref="leftContent">
                    <div v-if="$slots['left-header']" class="side-header" ref="leftHeader">
                        <slot name="left-header"/>
                    </div>
                    <div v-if="$slots['left-content']" class="side-main" :style="leftContentStyle">
                        <slot name="left-content"/>
                    </div>
                </div>
            </div>

            <!-- 主内容区域 -->
            <div v-if="hasMainSlot" class="page-container-main">
                <div class="main-content" ref="mainContent">
                    <div v-if="$slots.header" class="main-header" ref="mainHeader">
                        <slot name="header"/>
                    </div>
                    <div v-if="$slots.default" class="main-body" :style="mainContentStyle">
                        <slot />
                    </div>
                </div>
            </div>

            <!-- 右侧区域 -->
            <div v-if="hasRightSlot" class="page-container-side page-container-right">
                <div class="side-content" ref="rightContent">
                    <div v-if="$slots['right-header']" class="side-header" ref="rightHeader">
                        <slot name="right-header"/>
                    </div>
                    <div v-if="$slots['right-content']" class="side-main" :style="rightContentStyle">
                        <slot name="right-content"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PageMain",
    props: {
        title: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        },
        // 是否启用自动高度计算
        autoHeight: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            pageMainHeight: 500,
            pageLeftHeight: 500,
            pageRightHeight: 500
        }
    },
    computed: {
        hasLeftSlot() {
            return this.$slots['left-content'] || this.$slots['left-header'];
        },
        hasMainSlot() {
            return this.$slots.header || this.$slots.default;
        },
        hasRightSlot() {
            return this.$slots['right-content'] || this.$slots['right-header'];
        },
        bodyClass() {
            return {
                'has-header': this.$slots.head,
                'has-left': this.hasLeftSlot,
                'has-right': this.hasRightSlot
            };
        },
        leftContentStyle() {
            return this.autoHeight ? { height: `${this.pageLeftHeight}px`, overflowY: 'overlay' } : {};
        },
        mainContentStyle() {
            return this.autoHeight ? { height: `${this.pageMainHeight}px`, overflowY: 'overlay' } : {};
        },
        rightContentStyle() {
            return this.autoHeight ? { height: `${this.pageRightHeight}px`, overflowY: 'overlay' } : {};
        }
    },
    mounted() {
        if (this.autoHeight) {
            this.updateHeights();
        }
    },
    updated() {
        if (this.autoHeight) {
            this.$nextTick(() => {
                this.updateHeights();
            });
        }
    },
    methods: {
        updateHeights() {
            // 更新主区域高度
            if (this.hasMainSlot && this.$refs.mainContent) {
                let mainHeight = this.$refs.mainContent.offsetHeight;
                if (this.$slots.header && this.$refs.mainHeader) {
                    mainHeight -= this.$refs.mainHeader.offsetHeight;
                }
                this.pageMainHeight = Math.max(mainHeight - 2, 100);
            }

            // 更新左侧区域高度
            if (this.hasLeftSlot && this.$refs.leftContent) {
                let leftHeight = this.$refs.leftContent.offsetHeight;
                if (this.$slots['left-header'] && this.$refs.leftHeader) {
                    leftHeight -= this.$refs.leftHeader.offsetHeight;
                }
                this.pageLeftHeight = Math.max(leftHeight - 2, 100);
            }

            // 更新右侧区域高度
            if (this.hasRightSlot && this.$refs.rightContent) {
                let rightHeight = this.$refs.rightContent.offsetHeight;
                if (this.$slots['right-header'] && this.$refs.rightHeader) {
                    rightHeight -= this.$refs.rightHeader.offsetHeight;
                }
                this.pageRightHeight = Math.max(rightHeight - 2, 100);
            }
        },

        // 手动更新高度（供外部调用）
        refreshHeights() {
            if (this.autoHeight) {
                this.updateHeights();
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
	padding: 20px;
	border-radius: 8px;
    .page-container-header {
        flex-shrink: 0;
        height: 60px;
        z-index: 100;
        
        .header-content {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 0 20px;
            box-sizing: border-box;
        }
    }

    .page-container-body {
        flex: 1;
        display: flex;
        gap: 15px;
        position: relative;
        min-height: 0;

        &.has-header {
            height: calc(100% - 60px);
        }

        .page-container-side {
            flex-shrink: 0;
            width: 300px; // 可调整侧边栏宽度
            display: flex;
            flex-direction: column;
            
            .side-content {
                flex: 1;
                background-color: #ffffff;
                border: 1px solid #e6e6e6;
                border-radius: 2px;
                display: flex;
                flex-direction: column;
                min-height: 0;
                
                .side-header {
                    flex-shrink: 0;
                    min-height: 50px;
                    padding: 0 10px;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                }
                
                .side-main {
                    flex: 1;
                    min-height: 0;
                }
            }
        }

        .page-container-main {
            flex: 1;
            min-width: 590px;
            display: flex;
            flex-direction: column;
            min-height: 0;
            
            .main-content {
                flex: 1;
                background-color: #ffffff;
                border-radius: 2px;
                display: flex;
                flex-direction: column;
                min-height: 0;
                border-radius: 8px;
                .main-header {
                    flex-shrink: 0;
                    min-height: 50px;
                    padding: 0 10px;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                }
                
                .main-body {
                    flex: 1;
                    min-height: 0;
					border-radius: 8px;
                }
            }
        }
    }
}

// 响应式设计
@media (max-width: 1200px) {
    .page-container-body {
        flex-wrap: wrap;
        
        .page-container-side {
            width: 100% !important;
            max-width: 100%;
            
            .side-content {
                min-height: 200px;
            }
        }
        
        .page-container-main {
            min-width: 100% !important;
        }
    }
}
</style>